/**
 * SetEditor - Mobile-first set entry component for workout logging
 *
 * Features:
 * - Clean, compact design
 * - Comparison with previous workout
 * - Touch-friendly inputs
 * - Quick set templates
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, TrendingUp, TrendingDown, Minus, RefreshCw, Dumbbell, Check } from 'lucide-react';

const SetEditor = ({
  sets = [{ reps: '', weight: '' }],
  onSetsChange,
  weightUnit = 'kg',
  onWeightUnitChange,
  maxSets = 20,
  showQuickTemplates = true,
  comparisonData = null, // { date: string, sets: [{ weight, reps }] }
  className = '',
  // Companion mode props (for coach working out alongside student)
  companionMode = false,      // Show rotate button for companion mode
  isCompanionView = false,    // Currently showing coach's sets
  onToggleCompanion,          // Callback when rotate button clicked
}) => {
  const containerRef = useRef(null);

  // Debug log for companion mode
  console.log('[SetEditor] companionMode:', companionMode, 'isCompanionView:', isCompanionView, 'onToggleCompanion:', !!onToggleCompanion);

  // Add a new set (copies last set values but not completed state)
  const addSet = useCallback(() => {
    if (sets.length >= maxSets) return;
    const lastSet = sets[sets.length - 1] || { reps: '10', weight: '20' };
    onSetsChange([...sets, { reps: lastSet.reps, weight: lastSet.weight, completed: false }]);
  }, [sets, maxSets, onSetsChange]);

  // Remove a set
  const removeSet = useCallback((index) => {
    if (sets.length <= 1) return;
    onSetsChange(sets.filter((_, i) => i !== index));
  }, [sets, onSetsChange]);

  // Update a single field in a set
  const updateSetField = useCallback((index, field, value) => {
    const newSets = [...sets];
    newSets[index] = { ...newSets[index], [field]: value };
    onSetsChange(newSets);
  }, [sets, onSetsChange]);

  // Apply quick template (replace all sets)
  const applyTemplate = useCallback((count) => {
    const lastSet = sets[sets.length - 1] || { reps: '10', weight: '20' };
    const newSets = Array.from({ length: count }, () => ({
      reps: lastSet.reps || '10',
      weight: lastSet.weight || '20',
      completed: false,
    }));
    onSetsChange(newSets);
  }, [sets, onSetsChange]);

  // Get comparison for a specific set
  const getComparison = (index, currentWeight) => {
    if (!comparisonData?.sets?.[index]) return null;

    const prevWeight = comparisonData.sets[index].weight;
    const current = parseFloat(currentWeight) || 0;

    if (current === 0) return null;

    const diff = current - prevWeight;

    if (diff > 0) return { type: 'up', diff, prevWeight };
    if (diff < 0) return { type: 'down', diff, prevWeight };
    return { type: 'same', diff: 0, prevWeight };
  };

  // Render comparison badge
  const renderComparison = (comparison) => {
    if (!comparison) return null;

    const { type, diff } = comparison;

    if (type === 'up') {
      return (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs font-semibold rounded">
          <TrendingUp className="w-3 h-3" />
          +{diff.toFixed(1)}
        </span>
      );
    }
    if (type === 'down') {
      return (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs font-semibold rounded">
          <TrendingDown className="w-3 h-3" />
          {diff.toFixed(1)}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
        <Minus className="w-3 h-3" />
      </span>
    );
  };

  // Container styling based on companion mode
  const containerStyles = isCompanionView
    ? 'bg-violet-500/10 border border-violet-500/30 rounded-xl p-4'
    : '';

  const setRowStyles = isCompanionView
    ? 'bg-violet-900/30'
    : 'bg-gray-800/40';

  return (
    <motion.div
      ref={containerRef}
      className={`space-y-4 ${containerStyles} ${className}`}
      initial={false}
      animate={{
        rotateY: 0,
        transition: { duration: 0.4, ease: 'easeInOut' }
      }}
      style={{ perspective: 1000, transformStyle: 'preserve-3d' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isCompanionView ? (
            <>
              <Dumbbell className="w-5 h-5 text-violet-400" />
              <h3 className="text-lg font-semibold text-violet-300">Benim Setlerim</h3>
            </>
          ) : (
            <h3 className="text-lg font-semibold text-white">Setler</h3>
          )}
          <span className={`px-2 py-0.5 rounded-full text-sm ${isCompanionView ? 'bg-violet-500/20 text-violet-400' : 'bg-gray-800 text-gray-400'}`}>
            {sets.length}
          </span>
          {comparisonData && !isCompanionView && (
            <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded-full text-xs">
              vs önceki
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Companion mode toggle button */}
          {companionMode && onToggleCompanion && (
            <motion.button
              type="button"
              onClick={onToggleCompanion}
              whileTap={{ scale: 0.95 }}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                ${isCompanionView
                  ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30 hover:bg-violet-500/30'
                  : 'bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 hover:text-white'
                }
              `}
              title={isCompanionView ? "Öğrenci setlerine dön" : "Kendi setlerimi gir"}
            >
              <motion.div
                animate={{ rotate: isCompanionView ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <RefreshCw className="w-4 h-4" />
              </motion.div>
              <span className="hidden sm:inline">
                {isCompanionView ? 'Öğrenci' : 'Ben de'}
              </span>
            </motion.button>
          )}
          {/* Weight unit toggle */}
          {onWeightUnitChange && (
            <select
              value={weightUnit}
              onChange={(e) => onWeightUnitChange(e.target.value)}
              className={`px-2 py-1.5 border rounded-lg text-xs focus:outline-none ${
                isCompanionView
                  ? 'bg-violet-900/30 border-violet-500/30 text-violet-300 focus:border-violet-500'
                  : 'bg-gray-800 border-gray-700 text-gray-300 focus:border-orange-500'
              }`}
            >
              <option value="kg">kg</option>
              <option value="lb">lb</option>
            </select>
          )}
        </div>
      </div>

      {/* Quick Templates */}
      {showQuickTemplates && (
        <div className="flex items-center gap-2">
          {[3, 4, 5, 6].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => applyTemplate(num)}
              className={`
                flex-1 py-2 rounded-lg text-sm font-medium transition-all
                ${sets.length === num
                  ? isCompanionView
                    ? 'bg-violet-600 text-white'
                    : 'bg-orange-600 text-white'
                  : isCompanionView
                    ? 'bg-violet-900/30 text-violet-400 hover:bg-violet-900/50 hover:text-violet-300'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }
              `}
            >
              {num} set
            </button>
          ))}
        </div>
      )}

      {/* Sets List */}
      <div className="space-y-2">
        {sets.map((set, index) => {
          const comparison = getComparison(index, set.weight);
          const isCompleted = set.completed || false;

          return (
            <div
              key={index}
              className={`flex items-center gap-3 p-3 rounded-xl transition-all ${setRowStyles} ${isCompleted ? 'opacity-60' : ''}`}
            >
              {/* Completed Checkbox */}
              <button
                type="button"
                onClick={() => updateSetField(index, 'completed', !isCompleted)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-all ${
                  isCompleted
                    ? 'bg-green-600 text-white'
                    : isCompanionView
                      ? 'bg-violet-700/50 text-violet-300 hover:bg-violet-600/50'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                title={isCompleted ? 'Set tamamlandı' : 'Seti tamamla'}
              >
                {isCompleted ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span className="text-sm font-bold">{index + 1}</span>
                )}
              </button>

              {/* Reps Input */}
              <div className="flex-1">
                <input
                  type="number"
                  inputMode="numeric"
                  value={set.reps}
                  onChange={(e) => updateSetField(index, 'reps', e.target.value)}
                  placeholder="12"
                  className={`w-full px-3 py-2.5 border rounded-lg text-center text-lg font-medium focus:outline-none ${
                    isCompanionView
                      ? 'bg-violet-900/50 border-violet-600/50 text-white focus:border-violet-400'
                      : 'bg-gray-700 border-gray-600 text-white focus:border-orange-500'
                  }`}
                />
                <span className={`block text-center text-2xs mt-1 ${isCompanionView ? 'text-violet-400' : 'text-gray-500'}`}>tekrar</span>
              </div>

              {/* Separator */}
              <span className={`font-bold text-lg ${isCompanionView ? 'text-violet-500' : 'text-gray-600'}`}>×</span>

              {/* Weight Input with Comparison */}
              <div className="flex-1">
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  value={set.weight}
                  onChange={(e) => updateSetField(index, 'weight', e.target.value)}
                  placeholder="50"
                  className={`w-full px-3 py-2.5 border rounded-lg text-center text-lg font-medium focus:outline-none ${
                    isCompanionView
                      ? 'bg-violet-900/50 border-violet-600/50 text-violet-300 focus:border-violet-400'
                      : 'bg-gray-700 border-gray-600 text-orange-400 focus:border-orange-500'
                  }`}
                />
                <div className="flex items-center justify-center gap-1 mt-1">
                  <span className={`text-2xs ${isCompanionView ? 'text-violet-400' : 'text-gray-500'}`}>{weightUnit}</span>
                  {comparison && !isCompanionView && renderComparison(comparison)}
                </div>
              </div>

              {/* Delete Button */}
              {sets.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSet(index)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors flex-shrink-0 ${
                    isCompanionView
                      ? 'text-violet-400 hover:text-red-400 hover:bg-red-500/10'
                      : 'text-gray-500 hover:text-red-400 hover:bg-red-500/10'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Set Button */}
      <button
        type="button"
        onClick={addSet}
        disabled={sets.length >= maxSets}
        className={`w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
          isCompanionView
            ? 'border-violet-600/50 text-violet-400 hover:border-violet-500 hover:text-violet-300'
            : 'border-gray-700 text-gray-400 hover:border-orange-500 hover:text-orange-400'
        }`}
      >
        <Plus className="w-5 h-5" />
        <span className="font-medium">Set Ekle</span>
      </button>
    </motion.div>
  );
};

export default SetEditor;
